import type { ManifestFile, SessionManifest } from "../api/types";

const MAX_MANIFEST_BYTES = 1024 * 1024;
const SESSION_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const SHA256_PATTERN = /^[0-9a-f]{64}$/i;
const V1_SCHEMA = "deepvision.raw_session/v1";
const V2_SCHEMA = "deepvision.raw_session/v2";

export interface SessionDirectory {
  rootName: string;
  manifest: SessionManifest;
  rawManifest: string;
  manifestFile: File;
  sourceMarker: File;
  files: Map<string, File>;
  totalBytes: number;
}

export class DirectoryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DirectoryError";
  }
}

export async function readSessionDirectory(input: FileList | File[]): Promise<SessionDirectory> {
  const selected = Array.from(input);
  if (!selected.length) throw new DirectoryError("请选择一个 Session 目录");
  const entries = selected.map(toEntry);
  const manifestEntry = findRootFile(entries, "manifest.json");
  const markerEntry = findRootFile(entries, "_SUCCESS", manifestEntry.rootName);
  if (manifestEntry.file.size > MAX_MANIFEST_BYTES) throw new DirectoryError("manifest.json 超过 1 MiB 限制");
  const rawManifest = await readFileText(manifestEntry.file);
  const manifest = parseManifest(rawManifest);
  const files = validateDeclaredFiles(entries, manifest, manifestEntry.rootName);
  return {
    rootName: manifestEntry.rootName,
    manifest,
    rawManifest,
    manifestFile: manifestEntry.file,
    sourceMarker: markerEntry.file,
    files,
    totalBytes: [...files.values()].reduce((sum, file) => sum + file.size, 0),
  };
}

interface FileEntry {
  file: File;
  rootName: string;
  relativePath: string;
}

function toEntry(file: File): FileEntry {
  const raw = (file.webkitRelativePath || file.name).replaceAll("\\", "/");
  const parts = raw.split("/").filter(Boolean);
  if (!parts.length || parts.includes("..") || parts.includes(".")) throw new DirectoryError(`非法目录路径：${file.name}`);
  return {
    file,
    rootName: parts.length > 1 ? parts[0] : "",
    relativePath: parts.length > 1 ? parts.slice(1).join("/") : parts[0],
  };
}

function findRootFile(entries: FileEntry[], name: string, rootName?: string): FileEntry {
  const found = entries.filter((entry) => entry.relativePath === name && (rootName === undefined || entry.rootName === rootName));
  if (found.length !== 1) throw new DirectoryError(`目录根部必须且只能包含一个 ${name}`);
  return found[0];
}

function parseManifest(text: string): SessionManifest {
  let raw: unknown;
  try { raw = JSON.parse(stringifyNanoseconds(text)); } catch { throw new DirectoryError("manifest.json 不是有效 JSON"); }
  if (!isRecord(raw)) throw new DirectoryError("manifest.json 顶层必须是对象");
  validateManifestHeader(raw);
  const files = raw.files;
  if (!Array.isArray(files) || !files.length) throw new DirectoryError("manifest.files 必须是非空数组");
  const parsedFiles = files.map(parseManifestFile);
  validateFiles(parsedFiles, raw);
  return { ...raw, files: parsedFiles } as SessionManifest;
}

function validateManifestHeader(raw: Record<string, unknown>): void {
  if (raw.schema_version !== V1_SCHEMA && raw.schema_version !== V2_SCHEMA) {
    throw new DirectoryError("仅支持 deepvision.raw_session/v1 或 v2");
  }
  if (typeof raw.session_id !== "string" || !SESSION_ID_PATTERN.test(raw.session_id)) {
    throw new DirectoryError("manifest.session_id 不合法");
  }
  for (const field of ["name", "trigger_source", "outcome"] as const) {
    if (typeof raw[field] !== "string" || !raw[field].trim()) throw new DirectoryError(`manifest.${field} 必须是非空字符串`);
  }
  for (const field of ["started_at_ns", "stopped_at_ns"] as const) {
    if (typeof raw[field] !== "string" || !/^[0-9]+$/.test(raw[field])) throw new DirectoryError(`manifest.${field} 必须是纳秒整数字符串`);
  }
  if (typeof raw.duration_s !== "number" || !Number.isFinite(raw.duration_s)) throw new DirectoryError("manifest.duration_s 必须是数字");
  if (!("recording_schema" in raw)) throw new DirectoryError("manifest 缺少 recording_schema");
  if (raw.schema_version === V2_SCHEMA) validateV2Header(raw);
}

function parseManifestFile(value: unknown): ManifestFile {
  if (!isRecord(value)) throw new DirectoryError("manifest.files 条目必须是对象");
  const { role, path, size_bytes: size, sha256, content_type: contentType } = value;
  if (typeof role !== "string" || !role) throw new DirectoryError("manifest.files.role 不合法");
  if (typeof path !== "string" || !isSafeFileName(path)) throw new DirectoryError(`非法文件路径：${String(path)}`);
  if (!Number.isInteger(size) || (size as number) <= 0) throw new DirectoryError(`${path} 的 size_bytes 必须为正整数`);
  if (typeof sha256 !== "string" || !SHA256_PATTERN.test(sha256)) throw new DirectoryError(`${path} 的 SHA256 不合法`);
  if (typeof contentType !== "string" || !contentType) throw new DirectoryError(`${path} 缺少 content_type`);
  return { role, path, size_bytes: size as number, sha256: sha256.toLowerCase(), content_type: contentType };
}

function validateFiles(files: ManifestFile[], manifest: Record<string, unknown>): void {
  if (new Set(files.map((file) => file.path)).size !== files.length) throw new DirectoryError("manifest.files.path 不能重复");
  const rerunFiles = files.filter((file) => file.role === "rerun");
  const sessionId = manifest.session_id as string;
  const expected = `recording_${sessionId}.rrd`;
  const rrdAvailable = manifest.schema_version === V1_SCHEMA || manifest.rrd_available === true;
  if (rerunFiles.length !== (rrdAvailable ? 1 : 0)) {
    throw new DirectoryError(rrdAvailable ? `RRD 文件必须是 ${expected}` : "rrd_available=false 时不得声明 RRD 文件");
  }
  if (rerunFiles[0] && (rerunFiles[0].path !== expected || rerunFiles[0].content_type !== "application/vnd.rerun.rrd")) {
    throw new DirectoryError(`RRD 文件必须是 ${expected}，且 content_type 必须正确`);
  }
  if (manifest.schema_version === V2_SCHEMA) validateV2Roles(files, sessionId);
}

function validateV2Header(raw: Record<string, unknown>): void {
  if (typeof raw.rrd_available !== "boolean") throw new DirectoryError("manifest.rrd_available 必须是布尔值");
  if (!isRecord(raw.taxonomy) || typeof raw.taxonomy.id !== "string" || !raw.taxonomy.id.startsWith("deepvision.labels/")
    || typeof raw.taxonomy.sha256 !== "string" || !SHA256_PATTERN.test(raw.taxonomy.sha256)) {
    throw new DirectoryError("manifest.taxonomy 不合法");
  }
  if (!isRecord(raw.stats) || !isNonNegativeInteger(raw.stats.event_count) || !isNonNegativeInteger(raw.stats.controller_count)) {
    throw new DirectoryError("manifest.stats.event_count/controller_count 不合法");
  }
}

function validateV2Roles(files: ManifestFile[], sessionId: string): void {
  for (const role of ["session_metadata", "checksums", "events", "controller_stream"]) {
    if (files.filter((file) => file.role === role).length !== 1) throw new DirectoryError(`v2 必须且只能包含一个 ${role} 文件`);
  }
  if (files.filter((file) => file.role === "sidecar").length > 1) throw new DirectoryError("v2 最多包含一个 sidecar 文件");
  const expected: Record<string, [string, string]> = {
    session_metadata: [`session_${sessionId}.json`, "application/json"],
    checksums: ["checksums.sha256", "text/plain"],
    events: ["events.jsonl", "application/x-ndjson"],
    controller_stream: ["controller_stream.jsonl", "application/x-ndjson"],
    sidecar: [`session_${sessionId}.pkl`, "application/python-pickle"],
  };
  for (const file of files) {
    const shape = expected[file.role];
    if (shape && (file.path !== shape[0] || file.content_type !== shape[1])) throw new DirectoryError(`v2 ${file.role} 文件路径或类型不符合契约`);
  }
}

function validateDeclaredFiles(entries: FileEntry[], manifest: SessionManifest, rootName: string): Map<string, File> {
  const selected = new Map(entries.filter((entry) => entry.rootName === rootName).map((entry) => [entry.relativePath, entry.file]));
  const declared = new Map<string, File>();
  for (const item of manifest.files) {
    const file = selected.get(item.path);
    if (!file) throw new DirectoryError(`目录缺少 manifest 声明文件：${item.path}`);
    if (file.size !== item.size_bytes) throw new DirectoryError(`${item.path} 大小与 manifest 不一致`);
    declared.set(item.path, file);
  }
  return declared;
}

function isSafeFileName(path: string): boolean {
  return Boolean(path.trim()) && path !== "manifest.json" && path !== "_SUCCESS" && !path.includes("/") && !path.includes("\\");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): boolean {
  return Number.isInteger(value) && (value as number) >= 0;
}

function stringifyNanoseconds(text: string): string {
  return text.replace(/("(?:started_at_ns|stopped_at_ns)"\s*:\s*)([0-9]+)/g, "$1\"$2\"");
}

async function readFileText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new DirectoryError("manifest.json 读取失败"));
    reader.readAsText(file);
  });
}
